// components/home/reports/reportHelpers.js
// Small shared helpers for the report template components.

// Priority order here MUST exactly match bandIndexForLevel() in
// ScoreDial.js — compound phrases ("moderately low"/"moderately high")
// checked before the bare "moderate"/"low"/"high" words, otherwise the
// dial's needle and this pill's color can disagree on the same value (e.g.
// "Moderately Low" lighting up the amber needle but a green pill).
export function levelColor(level) {
  if (!level) return "#575555";
  const l = String(level).toLowerCase();
  if (l.includes("moderately low") || l.includes("mildly low")) return "#1a7a3c";
  if (l.includes("moderately high") || l.includes("mildly high")) return "#b3362c";
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative")) return "#b3362c";
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution")) return "#a1750f";
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition")) return "#1a7a3c";
  // COMT diplotype codes (e.g. "HPS", "APS/LPS") never match the words
  // above — pick the most severe allele present (worst-first).
  if (l.includes("hps")) return "#b3362c";
  if (l.includes("aps")) return "#a1750f";
  if (l.includes("lps")) return "#1a7a3c";
  return "#637cdb";
}

export function levelBg(level) {
  if (!level) return "#f3f3f3";
  const l = String(level).toLowerCase();
  if (l.includes("moderately low") || l.includes("mildly low")) return "#dcf5e3";
  if (l.includes("moderately high") || l.includes("mildly high")) return "#fbdada";
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative")) return "#fbdada";
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution")) return "#fdf1cf";
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition")) return "#dcf5e3";
  if (l.includes("hps")) return "#fbdada";
  if (l.includes("aps")) return "#fdf1cf";
  if (l.includes("lps")) return "#dcf5e3";
  return "#eef1f8";
}

// Drug Metabolism enzyme grade -> badge color, matching the color language
// Proove's own historical reports already used for this (green/orange/red/
// gray circle badges next to each enzyme) — see the "Proove Summary View"
// page of the old combined-panel PDF. Order matters: the ambiguous/
// indeterminate phrases are checked BEFORE the plain "decreased" check, so
// e.g. "Possible decreased metabolizer**" (a genuinely uncertain call, shown
// gray on the original reports) doesn't get misclassified as a confident
// "Decreased" (red) result just because that word appears in the phrase.
export function gradeColor(grade) {
  if (!grade) return { fg: "#64748b", bg: "#f1f5f9" };
  const g = String(grade).toLowerCase();
  if (g.includes("n/a") || g.includes("insufficient") || g.includes("indeterminate") || g.includes("possible decreased") || g.includes("possible increased"))
    return { fg: "#64748b", bg: "#f1f5f9" }; // gray — uncertain/insufficient
  if (g.includes("decreased") || g.includes("poor"))
    return { fg: "#b3362c", bg: "#fbdada" }; // red
  if (g.includes("increased") || g.includes("ultra"))
    return { fg: "#a1750f", bg: "#fdf1cf" }; // orange
  if (g.includes("normal") || g.includes("extensive"))
    return { fg: "#1a7a3c", bg: "#dcf5e3" }; // green
  return { fg: "#637cdb", bg: "#eef1f8" }; // neutral fallback (e.g. free-text clinical-implications sentences)
}

export function formatDemographicLabel(key) {
  return { name: "Patient Name", date_of_birth: "Date of Birth", gender: "Gender", ethnicity: "Ethnicity" }[key] || key;
}

// Colors ONLY explicit, unambiguous risk-level phrases inside the AI
// summary's <b> tags (e.g. "High Risk", "poor metabolizer") so they visually
// stand out from generic bold emphasis (gene names, drug names, etc.), which
// stays the plain navy accent from the .ai-summary-html CSS in ReportShell.
// Deliberately conservative — matches whole phrases only, never a bare
// "high"/"low" that could appear inside unrelated bold text.
const _RISK_PHRASE_COLORS = [
  [/\b(high risk|poor metabolizer)\b/i, "#b3362c"],
  [/\b(moderate risk|further evaluation needed|use with caution)\b/i, "#a1750f"],
  [/\b(low risk|normal metabolizer|extensive metabolizer)\b/i, "#1a7a3c"],
];

export function colorizeRiskTerms(html) {
  if (!html) return html;
  return html.replace(/<b>([^<]*)<\/b>/gi, (match, text) => {
    const hit = _RISK_PHRASE_COLORS.find(([re]) => re.test(text));
    return hit ? `<b style="color:${hit[1]}">${text}</b>` : match;
  });
}
